import { useEffect, useMemo, useRef, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AutoGrowTextarea } from '@/components/ui/auto-grow-textarea'
import { NeedsFromYouBadge } from '@/components/pack/NeedsFromYouBadge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  HelpCircle,
  Target,
  ListTree,
  CheckSquare,
  FileText,
  Check,
  Lightbulb,
  Plus,
  Undo2,
  X,
} from 'lucide-react'
import type {
  LivePlanJson,
  LivePlanSection,
  PlanChecklistItem,
  PlanChecklistKind,
  PlanFact,
  PlanLengthBasis,
  PlanResolvedEvidenceItem,
  PlanSuggestion,
} from '@/lib/api'
import type { RequirementDirectoryStatus } from '@/components/requirement-coverage/useRequirementDirectory'
// `planSectionDrafts` is the one allocator for key-point ids (shared with the
// store) and it carries the render-side machinery belt for ALREADY-PERSISTED
// polluted plans, so a hidden machinery line can never be confused with a
// rendered bullet — nor edited in place of one.
import { planSectionDrafts, visibleKeyPoints } from '@/lib/plan-edit'
import type { PlanEditDraft, PlanSectionDraft } from '@/lib/plan-edit'
import { cn } from '@/lib/utils'

// ===================
// Types
// ===================

/** The name map and its load state are one unit: a map with no status would let
 *  a still-filling map read as authoritative, pruning every row and reporting a
 *  confidently false "couldn't be matched" count. Pass both, or neither. */
type PlanCardRequirementNames =
  | {
      /** Caller isn't resolving titles — coverage rows show the raw id. */
      requirementNames?: undefined
      requirementNamesStatus?: undefined
    }
  | {
      /** Map of requirementId → human-readable title (coverageMap only carries ids). */
      requirementNames: Record<string, string>
      /** Only `loaded` makes the map authoritative; the other states suppress the
       *  coverage rows so a bare UUID is never shown as a title. */
      requirementNamesStatus: RequirementDirectoryStatus
    }

/** The six structure mutators, bound to a planId by the caller. */
export interface PlanCardEditHandlers {
  onUpdateSection: (
    sectionId: string,
    patch: { heading?: string; targetWordCount?: number }
  ) => void
  onMoveSection: (sectionId: string, direction: -1 | 1) => void
  onRemoveSection: (sectionId: string) => void
  /** Returns the id of the new key point so the card can focus its input. */
  onAddKeyPoint: (sectionId: string) => string | null
  onUpdateKeyPoint: (sectionId: string, keyPointId: string, text: string) => void
  onRemoveKeyPoint: (sectionId: string, keyPointId: string) => void
}

/** Editing is all-or-nothing, for the same reason the name map is: a card with
 *  three of the six mutators renders a rail whose buttons silently do nothing.
 *  `editDraft` is the store's in-progress draft — `null` until the writer's
 *  first edit forks one, at which point the card renders IT rather than the
 *  server plan. */
type PlanCardEditing =
  | {
      editDraft?: undefined
      onUpdateSection?: undefined
      onMoveSection?: undefined
      onRemoveSection?: undefined
      onAddKeyPoint?: undefined
      onUpdateKeyPoint?: undefined
      onRemoveKeyPoint?: undefined
    }
  | ({ editDraft: PlanEditDraft | null } & PlanCardEditHandlers)

type PlanCardProps = PlanCardRequirementNames & PlanCardEditing & {
  plan: LivePlanJson
  /**
   * Compliance item id → "REQ-n", from the bid's requirement directory. Labels
   * checklist requirements; a requirement with no entry shows its kind label
   * and text instead. Never required — omitting it only drops the numbers.
   */
  requirementRefs?: Record<string, string>
  /**
   * Bid the plan belongs to. Only drives the "Needs from you" badge in the
   * header — omitted (or null) simply hides it, which is what the read-only
   * and test renders want.
   */
  bidId?: string | null
  status?: 'pending' | 'accepted' | 'rejected'
  stale?: boolean
  isAccepting?: boolean
  onAccept?: () => void
  /**
   * Re-run plan generation. Drives BOTH the pending card's "Regenerate plan"
   * and the accepted card's "Re-plan section" — one store action, two entries.
   */
  onRegenerate?: () => void
  /**
   * A turn is already streaming or polling. `sendTurn` has no isStreaming guard
   * and the abort controller is a module singleton, so a second concurrent turn
   * orphans the first stream; after an accept, `isPolling` also makes `sendTurn`
   * reject outright. Both regenerate entry points are therefore hard-disabled
   * while this is true, each with its reason rendered beside the button.
   */
  isTurnBusy?: boolean
  /** Reject the plan outright. Confirmed first — a rejected plan is gone. */
  onReject?: () => void
  isRejecting?: boolean
  /** The draft has unsaved changes: shows the word total, Save and Revert. */
  isDirty?: boolean
  /** Commit the draft without accepting the plan. */
  onSave?: () => void
  isSaving?: boolean
  /** Discard the draft and return to the server plan. */
  onRevert?: () => void
  /** P3 §5 — "also worth including" suggestions (review-time; never in plan_json). */
  suggestions?: PlanSuggestion[]
  /** Accept folds the suggestion into the Brief (§5.4). */
  onAcceptSuggestion?: (suggestion: PlanSuggestion) => void
  /** Dismiss removes the suggestion (§5.4). */
  onDismissSuggestion?: (suggestionId: string) => void
  /** The suggestion id currently mid-accept (disables its Accept button). */
  isAcceptingSuggestionId?: string | null
  /**
   * The last suggestion dismissed on this card, if any. Suggestions ride the
   * event and are never persisted (§4.1), so a mis-click is otherwise final —
   * this drives the undo row.
   */
  dismissedSuggestion?: PlanSuggestion | null
  /** Put `dismissedSuggestion` back where it was. */
  onRestoreSuggestion?: () => void
  /**
   * Render the card collapsed behind this label (e.g. "Plan approved"); the
   * header becomes the toggle and the rest of the card opens beneath it. Used
   * for an approved plan put back from history — the card body is the normal
   * accepted rendering. Omitted: the card renders open, exactly as before.
   */
  collapsedLabel?: string
  /**
   * The plan is shown to read only, with no action row (R1 E1f): the AI panel
   * on a finalised or in-review document gives advice and makes no edits, so
   * nothing can be accepted, rejected or drafted from it there.
   */
  readOnly?: boolean
}

// ===================
// Collapsible Section
// ===================

interface CollapsibleSectionProps {
  title: string
  icon: React.ReactNode
  count?: number
  children: React.ReactNode
  defaultOpen?: boolean
}

function CollapsibleSection({
  title,
  icon,
  count,
  children,
  defaultOpen = false,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          className={cn(
            'w-full flex items-center justify-between px-3 py-2 text-left rounded-lg transition-colors',
            'hover:bg-muted/50'
          )}
        >
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{icon}</span>
            <span className="text-sm font-medium">{title}</span>
            {count !== undefined && (
              <Badge variant="secondary" className="text-xs">
                {count}
              </Badge>
            )}
          </div>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 py-2">{children}</CollapsibleContent>
    </Collapsible>
  )
}

// ===================
// Confidence Chip
// ===================

const CONFIDENCE_CHIP: Record<
  PlanResolvedEvidenceItem['confidenceBand'],
  string
> = {
  high: 'bg-[hsl(var(--status-success)/0.12)] text-[hsl(var(--status-success))]',
  medium: 'bg-[hsl(var(--status-warning)/0.12)] text-[hsl(var(--status-warning))]',
  low: 'bg-muted text-muted-foreground',
}

function ConfidenceChip({
  band,
}: {
  band: PlanResolvedEvidenceItem['confidenceBand']
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-lg px-1.5 py-0.5 text-xs font-medium capitalize',
        CONFIDENCE_CHIP[band]
      )}
    >
      {band}
    </span>
  )
}

// ===================
// Confirm dialog
// ===================

/**
 * One confirm shape for the card's two irreversible actions (reject the plan,
 * regenerate over unsaved edits). Built on the app's `AlertDialog` primitives,
 * same as `RegenerateProposalDialog` — there is no shared ConfirmDialog
 * component to reuse, and the copy differs enough that a generic one would be
 * all props and no meaning.
 */
function PlanConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  isDestructive,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  confirmLabel: string
  isDestructive?: boolean
  onConfirm: () => void
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          {/* The ONE place the destructive variant is allowed (design system:
              destructive lives inside confirmation dialogs, never in a row). */}
          <AlertDialogAction
            onClick={onConfirm}
            className={cn(
              isDestructive && 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
            )}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// ===================
// Structure row (variant C — hover/focus to reveal)
// ===================
//
// At rest the row is TEXT: reading a plan is the dominant use of a ~380px chat
// card, and six always-on inputs per section make it read as a form. The row
// under the pointer — or with focus inside it — swaps to the editing controls.
//
// Two things this costs, and how they are paid:
//
//  • **Height.** A revealed row is taller than a text one, and rows that grow
//    under the pointer shove their neighbours around. So every control's space
//    is RESERVED at rest: the icon rail keeps its 76px slot, the header line
//    keeps `min-h-7` (the height of the title field), each bullet keeps
//    `min-h-6` (the height of a key-point field) plus a 24px slot for its
//    remove button, and an empty `h-6` strip holds the "Add point" chip.
//    Reserved only when the card is editable — a read-only card renders
//    exactly as it did before.
//
//    The TEXT's height is handled differently, and this is the part that used
//    to be wrong. It was previously pinned by truncating the resting text to
//    one line, because the editing control was a one-line `<Input>` — so a
//    key point longer than the card was simply unreadable, in both states
//    (cut off at rest; scrolled out of a one-line box when revealed). The
//    editing controls are now auto-growing textareas, so both halves wrap to
//    the SAME number of lines and the heights match without cutting anything.
//    That equality is only true while the two halves share a box: see
//    `HEADING_BOX` / `KEY_POINT_BOX` below.
//  • **Keyboard.** Hover is not reachable from a keyboard, so the row is a tab
//    stop (`tabIndex=0`, labelled): tabbing to it reveals the controls, and the
//    next Tab lands on the title input. The row element itself never unmounts
//    across the swap, so nothing steals the focus that opened it.
//
// Reveal is decided by the CARD, and hover is gated on REAL POINTER MOVEMENT
// (`mousemove`), not on `mouseenter`.
//
// The problem this solves: after a move, the moved row is focus-within while
// the pointer now sits over a DIFFERENT section at the old y-position. Chrome
// and Firefox recompute hover after a layout change and re-dispatch
// `mouseover`/`mouseenter` — so that row would reveal too, putting a second
// live rail under a pointer that never moved, and the next click moves the
// wrong section. In browsers that don't re-dispatch, it doesn't. Browser-
// dependent is the wrong kind of correct.
//
// `mousemove` is the honest discriminator: a synthetic hover recomputation
// fires enter/over but NOT move, because nothing moved. Genuine movement fires
// it. So the rule is simply "the pointer reveals where the writer put it".
//
// Reveal is then the UNION of the hovered row and the focused row, not a
// single winner. Both orderings of a single winner are broken: focus-first
// kills hover card-wide whenever a rail action parks focus (Move re-homes,
// Remove point focuses Add, Add point autofocuses — the everyday mouse loop),
// and hover-first collapses the row a keyboard writer is typing in the moment
// the mouse is jostled, unmounting the focused input.
//
// The union's own hazard, stated honestly: `mousemove` means ANY real pointer
// movement, however small. One pixel of hand tremor counts. So after a move,
// the DISPLACED row — the one that slid into the moved row's old slot, under a
// pointer that hasn't gone anywhere — is one tremor away from revealing a live
// rail exactly where the writer is about to click again. "Move this up three
// positions" is click, tremor, click; the second click would move the wrong
// section. That is why the displaced row is SUPPRESSED
// (`suppressedHoverSectionId`) until the pointer genuinely leaves it: leaving
// is a real `mouseleave`, which a tremor inside the row cannot fake, so this
// costs no timing coupling. The writer who actually wants that row can still
// click it — clicking focuses the row, and focus reveals.

/**
 * Box metrics shared by a field's RESTING text and its editing textarea.
 *
 * Same font-size, line-height, horizontal padding and border width, in the
 * same width slot, is exactly what makes the two wrap to the same number of
 * lines — which is what lets the row swap between them without changing
 * height. Change one of these, change the other, or the row starts jumping
 * under the pointer again.
 *
 * The border is in here because it is 1px of box on each side that the text
 * does not get to use; only its COLOUR differs between the two states.
 */
const HEADING_BOX = 'border px-1 py-0.5 text-sm font-medium leading-5'
const KEY_POINT_BOX = 'border px-1 py-0.5 text-xs leading-4'

// ===================
// Section details (one plan format, technical design §3.5)
// ===================
//
// Read-only: purpose, checklist and the length basis. They render the SAME way
// at rest and while the row is revealed, so revealing a row never changes the
// height of this block (see the height notes above).

const CHECKLIST_KIND_LABEL: Record<PlanChecklistKind, string> = {
  requirement: 'Requirement',
  criterion: 'Criterion',
  brief: 'Brief',
  theme: 'Win theme',
  instruction: 'Instruction',
  limit: 'Limit',
}

const LENGTH_BASIS_LABEL: Record<PlanLengthBasis, string> = {
  tender_limit: "Length: set by the tender's limit",
  weighting: 'Length: follows the evaluation weighting',
  past_proposal: 'Length: based on a past proposal',
  user: 'Length: set by your team',
  judgement: "Length: the AI's estimate",
}

/** A fact's key as words ("project_director_name" → "project director name"): facts carry no label of their own. */
function factKeyInWords(key: string): string {
  return key.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
}

/** REQ-n for a numbered requirement; otherwise the item kind. Never the raw ref. */
function checklistItemLabel(
  item: PlanChecklistItem,
  requirementRefs: Record<string, string> | undefined
): string {
  if (item.kind === 'requirement' && item.ref) {
    const reqRef = requirementRefs?.[item.ref]
    if (reqRef) return reqRef
  }
  return CHECKLIST_KIND_LABEL[item.kind] ?? item.kind
}

/**
 * The basis to show beside the draft's word target. A target the writer has
 * changed is theirs — the server records `'user'` on save, and the card says so
 * before then rather than showing a basis the number no longer has.
 */
function displayedLengthBasis(
  section: PlanSectionDraft,
  stored: LivePlanSection | undefined
): PlanLengthBasis | undefined {
  if (!stored) return undefined
  if (stored.targetWordCount !== section.targetWordCount) return 'user'
  return stored.lengthBasis
}

function PlanSectionChecklist({
  heading,
  checklist,
  requirementRefs,
}: {
  heading: string
  checklist: PlanChecklistItem[]
  requirementRefs: Record<string, string> | undefined
}) {
  return (
    <ul className="mt-1.5 space-y-0.5" aria-label={`Checklist for ${heading}`}>
      {checklist.map((item, index) => (
        <li key={`${item.kind}-${item.ref ?? ''}-${index}`} className="flex items-start gap-1.5 text-meta">
          <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-xs font-normal tabular-nums">
            {checklistItemLabel(item, requirementRefs)}
          </Badge>
          <span className="min-w-0 flex-1 break-words">{item.text}</span>
        </li>
      ))}
    </ul>
  )
}

interface PlanSectionRowProps {
  section: PlanSectionDraft
  /** The stored section — the source of the read-only details. Absent on a legacy or unknown row. */
  details?: LivePlanSection | undefined
  requirementRefs?: Record<string, string> | undefined
  isFirst: boolean
  isLast: boolean
  /** Null when the card is read-only — no reveal, no tab stop, no reserved space. */
  editing: PlanCardEditHandlers | null
  /** The card decides; the row only reports what happened to it. */
  isRevealed: boolean
  /** Sections the last save attempt flagged (3F-a §5.3). Read by id ONLY. */
  conflictingSectionIds: ReadonlySet<string>
  onHoverChange: (isHovered: boolean) => void
  onFocusChange: (hasFocusWithin: boolean) => void
}

function PlanSectionRow({
  section,
  details,
  requirementRefs,
  isFirst,
  isLast,
  editing,
  isRevealed,
  conflictingSectionIds,
  onHoverChange,
  onFocusChange,
}: PlanSectionRowProps) {
  const [focusKeyPointId, setFocusKeyPointId] = useState<string | null>(null)
  /** Set by a move; consumed by the effect below once the reorder has committed. */
  const [pendingMoveFocus, setPendingMoveFocus] = useState<-1 | 1 | null>(null)
  const rowRef = useRef<HTMLDivElement>(null)
  const addPointRef = useRef<HTMLButtonElement>(null)
  const moveUpRef = useRef<HTMLButtonElement>(null)
  const moveDownRef = useRef<HTMLButtonElement>(null)
  /** Escape re-homes focus onto the row, which must NOT re-open it. */
  const suppressRevealRef = useRef(false)

  const isEditable = editing !== null
  const isEditing = isEditable && isRevealed
  const points = visibleKeyPoints(section)
  // A basis this build has no label for (a hand-edited row) shows nothing
  // rather than an empty line.
  const lengthBasis = displayedLengthBasis(section, details)
  const lengthBasisLabel = lengthBasis ? LENGTH_BASIS_LABEL[lengthBasis] : undefined
  // A function of MEMBERSHIP ONLY — never of `isRevealed`. See the marker note
  // on the row container below. An id matching no row degrades silently: the
  // two producers disagree on how many ids a duplicate heading yields.
  const isConflicting = conflictingSectionIds.has(section.sectionId)

  // `autoFocus` fires on EVERY mount, and a collapsed row remounts its inputs
  // the next time it is revealed. Left set, the id from an earlier "add point"
  // would yank focus out of whatever the writer is typing in (the chat
  // composer, say) merely because their pointer crossed this row. One reveal,
  // one auto-focus.
  useEffect(() => {
    if (!isEditing) setFocusKeyPointId(null)
  }, [isEditing])

  // Re-home focus after a move. Both `onMoveSection` and this flag are set in
  // the same handler, so they land in one commit — by the time the effect runs,
  // the row has been reordered and its DOM node moved (which browsers treat as
  // a blur). Without this the keyboard writer is dropped to <body>, and the
  // pointer now sits over a DIFFERENT section whose rail is in the same place.
  useEffect(() => {
    if (pendingMoveFocus === null) return
    setPendingMoveFocus(null)
    // The button just used may now be disabled (the section reached an edge) —
    // fall back to its twin, then to the row itself.
    const preferred = pendingMoveFocus === -1
      ? [moveUpRef.current, moveDownRef.current]
      : [moveDownRef.current, moveUpRef.current]
    const target = preferred.find((button) => button && !button.disabled)
    if (target) target.focus()
    else rowRef.current?.focus()
  }, [pendingMoveFocus])

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    // Focus moving BETWEEN controls inside the row must not collapse it — the
    // collapse would unmount the control that just took focus. `relatedTarget`
    // is null when the focused node was removed (deleting a key point) or when
    // the browser blurs a reordered node; those paths re-home focus themselves,
    // so treat null as "still inside".
    const next = event.relatedTarget
    if (next && !event.currentTarget.contains(next)) onFocusChange(false)
  }

  // Escape is the keyboard writer's exit: leaving a revealed row otherwise
  // means tabbing through every control in it. Focus lands back on the row
  // container so Tab carries on from here rather than from <body>, and the
  // suppression flag stops that focus from re-opening what was just closed.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || !isEditing) return
    event.stopPropagation()
    onHoverChange(false)
    onFocusChange(false)
    // Focus LAST, and behind the flag: re-homing focus onto the row fires this
    // component's own `onFocus`, whose `onFocusChange(true)` would otherwise be
    // the final update in the batch and re-open what Escape just closed.
    //
    // Cleared UNCONDITIONALLY on the next line, because `focus()` is a no-op
    // when the row is already the active element (Escape pressed straight after
    // tabbing to the row — the most natural keyboard exit). It dispatches
    // nothing, so `handleFocus` never runs to consume the flag; a flag left set
    // swallows the NEXT reveal entirely, and makes the post-move focus re-home
    // fail. `focus()` dispatches synchronously, so clearing here is safe.
    suppressRevealRef.current = true
    rowRef.current?.focus()
    suppressRevealRef.current = false
  }

  const handleFocus = () => {
    if (suppressRevealRef.current) {
      suppressRevealRef.current = false
      return
    }
    onFocusChange(true)
  }

  const handleMove = (direction: -1 | 1) => {
    editing?.onMoveSection(section.sectionId, direction)
    setPendingMoveFocus(direction)
  }

  const handleAddPoint = () => {
    const keyPointId = editing?.onAddKeyPoint(section.sectionId)
    if (keyPointId) setFocusKeyPointId(keyPointId)
  }

  const handleRemovePoint = (keyPointId: string) => {
    editing?.onRemoveKeyPoint(section.sectionId, keyPointId)
    // The button that was clicked is now unmounted; park focus on the one
    // control in the row that is always there while editing.
    addPointRef.current?.focus()
  }

  return (
    <div
      ref={rowRef}
      // The conflict marker is a BACKGROUND SWAP, an `aria-invalid` and a
      // suffix on the accessible NAME — nothing else (3F-a §5.3). Everything
      // geometric is forbidden here: a border adds 2px to a container that has
      // none, a ring collides with the focus ring one line down, and a line of
      // text (or an icon) adds a line. The set is cleared by the writer's next
      // edit, so a height-bearing marker would vanish on the first keystroke
      // and jump every row below it under a stationary caret.
      //
      // The name suffix is why colour is not the only differentiator:
      // `aria-invalid` is NOT a supported state for `role="group"`, so AT will
      // most likely ignore it, and a tint alone is the design system's
      // "colour as only differentiator" anti-pattern. A string costs no
      // geometry, which an icon or a status line would not.
      className={cn(
        'rounded p-2',
        isConflicting ? 'bg-destructive/10' : 'bg-muted/50',
        isEditable && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand))]'
      )}
      aria-invalid={isConflicting || undefined}
      {...(isEditable
        ? {
            role: 'group',
            'aria-label': isConflicting
              ? `Edit section: ${section.heading}, has unresolved conflicts`
              : `Edit section: ${section.heading}`,
            tabIndex: 0,
            // `mousemove`, not `mouseenter` — see the note above. Cheap despite
            // the firing rate: the card's setter returns the identical string
            // when the row is already hovered, so React bails out of the render.
            onMouseMove: () => onHoverChange(true),
            onMouseLeave: () => onHoverChange(false),
            onFocus: handleFocus,
            onBlur: handleBlur,
            onKeyDown: handleKeyDown,
          }
        : {})}
    >
      {/* `items-start`, not `items-center`: a heading that wraps to two lines
          would otherwise drag the word count and the icon rail down to its
          vertical middle, away from the line they belong to. */}
      <div className="flex min-h-7 items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          {isEditing ? (
            <AutoGrowTextarea
              value={section.heading}
              onChange={(e) =>
                editing?.onUpdateSection(section.sectionId, { heading: e.target.value })
              }
              singleLine
              className={cn(
                HEADING_BOX,
                'min-w-0 flex-1 break-words rounded-md border-transparent bg-transparent hover:border-border focus-visible:border-border focus-visible:outline-none'
              )}
              aria-label="Section title"
            />
          ) : (
            <span
              className={cn(
                'min-w-0 flex-1 break-words',
                // A read-only card has no editing control to match, so it keeps
                // the plain text box it always had — no borrowed padding.
                isEditable ? cn(HEADING_BOX, 'border-transparent') : 'text-sm font-medium'
              )}
            >
              {section.heading}
            </span>
          )}
          {isEditing ? (
            <div className="flex shrink-0 items-center gap-1 text-meta">
              <Input
                type="number"
                value={section.targetWordCount}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10)
                  // A cleared field becomes 0, not NaN: NaN renders as the
                  // literal "NaN" in a controlled input. 0 is caught by
                  // `validatePlanDraft` with copy the writer can act on.
                  editing?.onUpdateSection(section.sectionId, {
                    targetWordCount: Number.isNaN(value) ? 0 : value,
                  })
                }}
                className="h-7 w-20 px-1 text-right text-xs"
                aria-label="Word count"
              />
              words
            </div>
          ) : (
            <span className="shrink-0 text-meta">
              ~{section.targetWordCount} words
            </span>
          )}
        </div>
        {/* Reserved rail — 3 × 24px + 2 × 2px gap. Empty at rest so the row
            keeps its height when the buttons appear. */}
        {isEditable && (
          <div className="flex h-6 w-[4.75rem] shrink-0 items-center justify-end gap-0.5">
            {isEditing && (
              <>
                {/* `title` alone is the LAST-RESORT accessible-name source and
                    some AT setups suppress it — icon-only buttons carry an
                    explicit aria-label, and keep title for the tooltip. */}
                <Button
                  ref={moveUpRef}
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  disabled={isFirst}
                  onClick={() => handleMove(-1)}
                  title="Move up"
                  aria-label="Move up"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  ref={moveDownRef}
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  disabled={isLast}
                  onClick={() => handleMove(1)}
                  title="Move down"
                  aria-label="Move down"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 hover:text-destructive"
                  onClick={() => editing?.onRemoveSection(section.sectionId)}
                  title="Remove section"
                  aria-label="Remove section"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {details?.purpose && (
        <p className="mt-1 break-words text-meta">{details.purpose}</p>
      )}

      {points.length > 0 && (
        <ul className="mt-1.5 space-y-0.5">
          {points.map((point) =>
            /* Both states are the SAME three-slot row — bullet, text, 24px —
               with the same gap. That is deliberate and load-bearing: the text
               slot has to be the identical width in both, or the two halves
               wrap at different words and the row still changes height. So the
               bullet stays visible while editing, and the remove button's slot
               is held open (empty) at rest.

               `min-h-6` is a FLOOR for the remove button, not the text height —
               the text is free to wrap to as many lines as it needs, because
               the textarea will wrap to exactly the same number. */
            isEditing ? (
              <li key={point.id} className="flex min-h-6 items-start gap-1.5 text-xs">
                <span
                  aria-hidden="true"
                  className={cn(KEY_POINT_BOX, 'select-none border-transparent px-0 text-muted-foreground')}
                >
                  •
                </span>
                <AutoGrowTextarea
                  value={point.text}
                  onChange={(e) =>
                    editing?.onUpdateKeyPoint(section.sectionId, point.id, e.target.value)
                  }
                  singleLine
                  className={cn(KEY_POINT_BOX, 'min-w-0 flex-1 break-words rounded-md border-input bg-card focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring')}
                  aria-label="Key point"
                  autoFocus={point.id === focusKeyPointId}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 hover:text-destructive"
                  onClick={() => handleRemovePoint(point.id)}
                  title="Remove point"
                  aria-label="Remove point"
                >
                  <X className="h-3 w-3" />
                </Button>
              </li>
            ) : (
              <li
                key={point.id}
                className={cn(
                  'flex gap-1.5 text-meta',
                  isEditable && 'min-h-6 items-start'
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn('select-none', isEditable && cn(KEY_POINT_BOX, 'border-transparent px-0'))}
                >
                  •
                </span>
                <span
                  className={cn(
                    'min-w-0 flex-1 break-words',
                    // Read-only cards never had an editing half to match, so
                    // they keep the plain wrapping text they always had.
                    isEditable && cn(KEY_POINT_BOX, 'border-transparent')
                  )}
                >
                  {point.text}
                </span>
                {/* Holds the remove button's place so revealing it adds nothing. */}
                {isEditable && <span aria-hidden="true" className="h-6 w-6 shrink-0" />}
              </li>
            )
          )}
        </ul>
      )}

      {isEditable && (
        <div className="mt-1.5 flex h-6 items-center">
          {isEditing && (
            <button
              ref={addPointRef}
              type="button"
              onClick={handleAddPoint}
              className="inline-flex items-center gap-1 rounded-lg border border-dashed border-border px-1.5 py-0.5 text-meta hover:bg-muted"
            >
              <Plus className="h-3 w-3" />
              Add point
            </button>
          )}
        </div>
      )}

      {details?.checklist && details.checklist.length > 0 && (
        <PlanSectionChecklist
          heading={section.heading}
          checklist={details.checklist}
          requirementRefs={requirementRefs}
        />
      )}

      {lengthBasisLabel && <p className="mt-1.5 text-meta">{lengthBasisLabel}</p>}
    </div>
  )
}

// ===================
// Main Component
// ===================

export function PlanCard({
  plan,
  bidId,
  status,
  stale,
  isAccepting,
  onAccept,
  onRegenerate,
  isTurnBusy,
  onReject,
  isRejecting,
  isDirty,
  onSave,
  isSaving,
  onRevert,
  editDraft,
  onUpdateSection,
  onMoveSection,
  onRemoveSection,
  onAddKeyPoint,
  onUpdateKeyPoint,
  onRemoveKeyPoint,
  requirementNames,
  requirementNamesStatus,
  requirementRefs,
  suggestions,
  onAcceptSuggestion,
  onDismissSuggestion,
  isAcceptingSuggestionId,
  dismissedSuggestion,
  onRestoreSuggestion,
  collapsedLabel,
  readOnly = false,
}: PlanCardProps) {
  const planSuggestions = suggestions ?? []
  const [isExpanded, setIsExpanded] = useState(false)
  const [confirmReject, setConfirmReject] = useState(false)
  const [confirmRegenerate, setConfirmRegenerate] = useState(false)
  const [confirmReplan, setConfirmReplan] = useState(false)
  const sectionsRef = useRef<HTMLDivElement>(null)

  // Reveal state lives here, not per row. `hoveredSectionId` only ever moves on
  // real pointer movement (the row listens to `mousemove`), so a hover
  // recomputation after a reorder cannot take it. See the note on
  // `PlanSectionRow` for why this is a union rather than a single winner.
  const [hoveredSectionId, setHoveredSectionId] = useState<string | null>(null)
  const [focusedSectionId, setFocusedSectionId] = useState<string | null>(null)
  /** The row a move just slid under a stationary pointer. See the note above. */
  const [suppressedHoverSectionId, setSuppressedHoverSectionId] = useState<string | null>(null)

  // The draft is the source of truth once one exists — it carries the writer's
  // uncommitted structure AND the key-point ids the mutators address. With no
  // draft yet, the same allocator mints the ids the store is about to mint, so
  // the first edit names the right key point.
  const renderSections = useMemo(
    () => editDraft?.sections ?? planSectionDrafts(plan).sections,
    [editDraft, plan]
  )
  // The read-only section details come from the stored plan the draft forked
  // from (or the server plan), matched by id — the draft holds only what the
  // card can edit.
  const storedSectionById = useMemo(
    () => new Map(((editDraft?.base ?? plan).sections ?? []).map((section) => [section.sectionId, section])),
    [editDraft, plan]
  )

  const isDecided = status === 'accepted' || status === 'rejected'

  // DECISION (3A item 7) — a STALE plan stays editable and stays rejectable.
  // Only Accept is blocked while stale, because accepting would draft from a
  // plan the brief has already moved past. Editing is the opposite case: what
  // went stale (a new pin, a changed requirement) is often exactly what the
  // writer is about to fix by hand, and a card that goes read-only the moment
  // the brief changes would force a full regenerate to change one heading.
  // Reject is likewise unaffected — a stale plan is MORE likely to deserve
  // rejecting, not less. Regenerate stays enabled either way.
  //
  // The props are a discriminated union — narrowing one handler narrows all six.
  const editing: PlanCardEditHandlers | null =
    onUpdateSection === undefined || isDecided
      ? null
      : {
          onUpdateSection,
          onAddKeyPoint,
          // A move slides the section at `index + direction` into the slot the
          // pointer is sitting in. Suppress that row's hover-reveal until the
          // pointer genuinely leaves it, so a tremor can't put a live rail
          // under the writer's next click.
          onMoveSection: (sectionId: string, direction: -1 | 1) => {
            const index = renderSections.findIndex((s) => s.sectionId === sectionId)
            const displaced = index < 0 ? undefined : renderSections[index + direction]
            onMoveSection(sectionId, direction)
            if (displaced) setSuppressedHoverSectionId(displaced.sectionId)
          },
          onUpdateKeyPoint,
          onRemoveKeyPoint,
          // Removing a section unmounts the row that held focus. Re-home it on
          // the sections container so a keyboard writer isn't dropped to <body>.
          onRemoveSection: (sectionId: string) => {
            onRemoveSection(sectionId)
            sectionsRef.current?.focus()
          },
        }

  // Rides the existing `editDraft` prop — no new wiring through the panel.
  const conflictingSectionIds = useMemo(
    () => new Set(editDraft?.conflictingSectionIds ?? []),
    [editDraft]
  )

  const totalWords = renderSections.reduce(
    (sum, section) => sum + (Number.isFinite(section.targetWordCount) ? section.targetWordCount : 0),
    0
  )

  const sectionHeadingById = new Map(
    renderSections.map((section) => [section.sectionId, section.heading])
  )
  // coverageMap carries requirement ids only. A bare UUID is never a useful
  // title, so rows are only rendered once the caller's name map is resolved:
  //  • no map at all      → caller isn't resolving titles; render ids verbatim.
  //  • map idle (no bid)  → titles are unobtainable; omit the block entirely.
  //  • map still loading  → one muted "loading" line, no rows.
  //  • map failed to load → one muted failure line, no rows.
  //  • map loaded         → prune rows whose id doesn't resolve (the LLM can
  //    emit stale/hallucinated ids) and tell the writer how many were dropped.
  // A stored plan is not guaranteed to carry a coverage map — treat none as empty.
  const allCoverageEntries = Object.entries(plan.coverageMap ?? {})
  const namesIdle = requirementNamesStatus === 'idle'
  const namesLoading = requirementNamesStatus === 'loading'
  const namesFailed = requirementNamesStatus === 'failed'
  const namesLoaded = requirementNamesStatus === 'loaded'
  const coverageEntries = requirementNamesStatus && !namesLoaded
    ? []
    : allCoverageEntries.filter(
        ([requirementId]) => !namesLoaded || requirementNames?.[requirementId]
      )
  const prunedCoverageCount = namesLoaded
    ? allCoverageEntries.length - coverageEntries.length
    : 0
  const resolvedEvidence = plan.resolvedEvidence ?? []
  // Rich planner fields — absent on almost every plan, so both blocks omit
  // themselves entirely when empty (same rule as Coverage and Evidence, §2.4).
  const riskFlags = plan.riskFlags ?? []
  const openQuestions = plan.openQuestions ?? []
  // Unconfirmed facts: writers are told not to state them. `question_raised`
  // is still unconfirmed — a question is open for it.
  const factsToConfirm: PlanFact[] = (plan.facts ?? []).filter(
    (fact) => fact.status === 'to_confirm' || fact.status === 'question_raised'
  )

  // Header — the plan's size at a glance. The total is SUPPRESSED while
  // dirty: the "Unsaved edits — N words total" caption already owns the same
  // figure, and one number in two places is worse than none.
  const header = (
    <div className="flex items-center justify-between gap-2 px-3 py-2">
      <span className="text-sm font-medium">Plan</span>
      <div className="flex items-center gap-2">
        <NeedsFromYouBadge bidId={bidId} />
        {!isDirty && (
          <span className="text-meta">
            {totalWords.toLocaleString('en-AU')} words total
          </span>
        )}
      </div>
    </div>
  )

  const body = (
    <>
      {/* Goal — always */}
      <div className="p-3">
        <div className="flex items-start gap-2">
          <Target className="h-4 w-4 text-primary mt-0.5 shrink-0" />
          <div>
            <span className="text-xs font-medium text-muted-foreground">Goal</span>
            <p className="text-sm mt-0.5">{editDraft?.goal ?? plan.goal}</p>
          </div>
        </div>
      </div>

      {/* Structure — always */}
      <CollapsibleSection
        title="Structure"
        icon={<ListTree className="h-4 w-4" />}
        count={renderSections.length}
        defaultOpen
      >
        <div ref={sectionsRef} tabIndex={-1} className="space-y-2 focus:outline-none">
          {renderSections.map((section, index) => (
            <PlanSectionRow
              key={section.sectionId}
              section={section}
              details={storedSectionById.get(section.sectionId)}
              requirementRefs={requirementRefs}
              isFirst={index === 0}
              isLast={index === renderSections.length - 1}
              editing={editing}
              isRevealed={
                (hoveredSectionId === section.sectionId
                  && suppressedHoverSectionId !== section.sectionId)
                || focusedSectionId === section.sectionId
              }
              conflictingSectionIds={conflictingSectionIds}
              onHoverChange={(isHovered) => {
                setHoveredSectionId((current) =>
                  isHovered
                    ? section.sectionId
                    : current === section.sectionId
                      ? null
                      : current
                )
                setSuppressedHoverSectionId((current) => {
                  if (current === null) return current
                  // The pointer left the suppressed row: the hazard is over.
                  if (!isHovered) return current === section.sectionId ? null : current
                  // …or it moved onto a DIFFERENT row, which it can only have
                  // done by travelling. Also over.
                  return current === section.sectionId ? current : null
                })
              }}
              onFocusChange={(hasFocusWithin) =>
                setFocusedSectionId((current) =>
                  hasFocusWithin
                    ? section.sectionId
                    : current === section.sectionId
                      ? null
                      : current
                )
              }
            />
          ))}
        </div>
      </CollapsibleSection>

      {/* Requirement coverage — only when non-empty (§2.4) and resolvable */}
      {allCoverageEntries.length > 0 && !namesIdle && (
        <CollapsibleSection
          title="Requirement coverage"
          icon={<CheckSquare className="h-4 w-4" />}
          // No badge while unresolved (or when every row was pruned) — a count
          // that shrinks after load, or a bare "0", is worse than none.
          {...(coverageEntries.length > 0 ? { count: coverageEntries.length } : {})}
        >
          <div className="space-y-2">
            {namesLoading && (
              <p className="text-meta">Loading requirement names…</p>
            )}
            {namesFailed && (
              <p className="text-meta">Couldn't load requirement names</p>
            )}
            {coverageEntries.map(([requirementId, sectionIds]) => (
              <div key={requirementId} className="rounded bg-muted/50 p-2">
                <p className="text-xs font-medium mb-1">{requirementNames?.[requirementId] ?? requirementId}</p>
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-meta">Addressed in:</span>
                  {sectionIds.map((sectionId) => (
                    <Badge
                      key={sectionId}
                      variant="secondary"
                      className="text-xs font-normal"
                    >
                      {sectionHeadingById.get(sectionId) ?? sectionId}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
            {prunedCoverageCount > 0 && (
              <p className="text-meta">
                {prunedCoverageCount === 1
                  ? "1 coverage claim referenced a requirement that couldn't be matched"
                  : `${prunedCoverageCount} coverage claims referenced requirements that couldn't be matched`}
              </p>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Evidence it'll use — only when non-empty (§2.4) */}
      {resolvedEvidence.length > 0 && (
        <CollapsibleSection
          title="Evidence it'll use"
          icon={<FileText className="h-4 w-4" />}
          count={resolvedEvidence.length}
        >
          <div className="space-y-1.5">
            {resolvedEvidence.map((item) => (
              <div
                key={item.documentId}
                className="flex items-center justify-between gap-2 rounded bg-muted/50 p-2"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm">{item.documentName}</span>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {item.pinned ? (
                    <span className="inline-flex items-center gap-0.5 text-xs font-medium text-[hsl(var(--brand))]">
                      <Check className="h-3 w-3" />
                      honored
                    </span>
                  ) : (
                    <span className="text-meta">AI-found</span>
                  )}
                  <ConfidenceChip band={item.confidenceBand} />
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Facts to confirm — only when non-empty (§2.4). Open by default: these
          are gaps the draft will not fill on its own. */}
      {factsToConfirm.length > 0 && (
        <CollapsibleSection
          title="Facts to confirm"
          icon={<ClipboardCheck className="h-4 w-4" />}
          count={factsToConfirm.length}
          defaultOpen
        >
          <ul className="space-y-1">
            {factsToConfirm.map((fact, index) => {
              // `display` is the person's line (it names the fact itself); `value` may end with an
              // instruction for the writers. A fact without one is named by its key in words.
              const line = (fact.display ?? fact.value).trim()
              return (
                <li key={`${fact.key}-${index}`} className="flex flex-wrap items-baseline gap-x-1.5 text-meta">
                  {fact.display === undefined && (
                    <span className="font-medium text-foreground">{factKeyInWords(fact.key)}</span>
                  )}
                  {line.length > 0 && <span className="min-w-0 break-words">{line}</span>}
                  {fact.status === 'question_raised' && (
                    <Badge variant="secondary" className="px-1.5 py-0 text-xs font-normal">
                      Question raised
                    </Badge>
                  )}
                </li>
              )
            })}
          </ul>
        </CollapsibleSection>
      )}

      {/* Risk flags — only when non-empty (§2.4). Plan-level `string[]` set by
          `set_plan`; NOT the `OverviewRiskFlag` that `RiskFlagsBanner` renders. */}
      {riskFlags.length > 0 && (
        <CollapsibleSection
          title="Risk flags"
          icon={<AlertTriangle className="h-4 w-4" />}
          count={riskFlags.length}
          defaultOpen
        >
          <ul className="space-y-1">
            {riskFlags.map((flag) => (
              <li key={flag} className="flex gap-1.5 text-meta">
                <span aria-hidden="true" className="select-none">•</span>
                <span className="min-w-0">{flag}</span>
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {/* Open questions — only when non-empty (§2.4) */}
      {openQuestions.length > 0 && (
        <CollapsibleSection
          title="Open questions"
          icon={<HelpCircle className="h-4 w-4" />}
          count={openQuestions.length}
          defaultOpen
        >
          <ul className="space-y-1">
            {openQuestions.map((question) => (
              <li key={question} className="flex gap-1.5 text-meta">
                <span aria-hidden="true" className="select-none">•</span>
                <span className="min-w-0">{question}</span>
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {/* Also worth including — only when non-empty (§2.4 omit-empty-blocks).
          The band also has to survive dismissing the LAST suggestion, or the
          undo row disappears in the same frame as the thing it undoes. */}
      {(planSuggestions.length > 0 || dismissedSuggestion) && (
        <CollapsibleSection
          title="Also worth including"
          icon={<Lightbulb className="h-4 w-4" />}
          count={planSuggestions.length}
          defaultOpen
        >
          <div className="flex flex-col gap-1.5">
            {planSuggestions.map((suggestion) => (
              <div
                key={suggestion.id}
                className="flex items-start justify-between gap-2 rounded-lg border border-dashed border-[hsl(var(--brand)/0.4)] bg-[hsl(var(--brand)/0.04)] p-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{suggestion.title}</p>
                  {suggestion.reason && (
                    <p className="mt-0.5 text-meta">{suggestion.reason}</p>
                  )}
                </div>
                {/* Labelled, not bare icons: "+" and "×" on an advisory chip
                    read as "expand" and "close the chip", which is the opposite
                    of what they do. `ai-ghost` + `ghost` keeps the pair even —
                    the iris marks the AI-authored action without a box. Each
                    aria-label leads with the visible word (WCAG 2.5.3) and
                    names its suggestion, so a screen reader hears which chip. */}
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ai-ghost"
                    size="sm"
                    aria-label={`Add ${suggestion.title}`}
                    disabled={isAcceptingSuggestionId === suggestion.id}
                    onClick={() => onAcceptSuggestion?.(suggestion)}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Add
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`Dismiss ${suggestion.title}`}
                    onClick={() => onDismissSuggestion?.(suggestion.id)}
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    Dismiss
                  </Button>
                </div>
              </div>
            ))}
            {dismissedSuggestion && onRestoreSuggestion && (
              <div className="flex items-center justify-between gap-2 rounded-lg bg-muted/40 px-2 py-1">
                <span className="min-w-0 truncate text-meta">
                  Dismissed “{dismissedSuggestion.title}”
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0"
                  onClick={onRestoreSuggestion}
                >
                  <Undo2 className="h-3.5 w-3.5 mr-1" />
                  Undo
                </Button>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Action row */}
      {!isDecided && !readOnly && (
        <div className="p-3 flex items-center justify-between gap-2">
          {/* One left slot, and the stale warning wins it: "regenerate before
              drafting" is a blocker (Accept is disabled), while the edited
              caption is a running total. Losing the blocker to a tally would
              leave a disabled Accept with nothing explaining it. */}
          {stale ? (
            <span className="text-xs text-[hsl(var(--status-warning))]">
              Brief updated — regenerate before drafting
            </span>
          ) : isDirty ? (
            // "Edited" reads like a property of the saved plan. The writer has
            // to be told the changes are NOT committed yet — a Save button they
            // haven't noticed doesn't help them.
            <span className="text-meta">
              Unsaved edits — {totalWords.toLocaleString('en-AU')} words total
            </span>
          ) : null}
          <div className="ml-auto flex items-center gap-2">
            {isDirty && onRevert && (
              <Button variant="ghost" size="sm" disabled={isSaving || isAccepting} onClick={onRevert}>
                Revert
              </Button>
            )}
            {isDirty && onSave && (
              // `secondary` (tonal fill, no border), not `ghost`: the "Unsaved
              // edits" caption is telling the writer to act, so the action it
              // points at has to stay visible at rest without taking a border.
              <Button
                variant="secondary"
                size="sm"
                disabled={isSaving || isAccepting}
                onClick={onSave}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </Button>
            )}
            {onReject && (
              <Button
                variant="ghost"
                size="sm"
                className="hover:text-destructive"
                disabled={isRejecting || isSaving || isAccepting}
                onClick={() => setConfirmReject(true)}
              >
                {isRejecting ? 'Rejecting…' : 'Reject'}
              </Button>
            )}
            {/* No `stale &&` gate (3D §3.1): a writer who dislikes the plan
                shouldn't have to wait for the brief to move before they can ask
                for another. `onRegenerate &&` is retained deliberately — the
                prop is optional, and historical transcript cards are rendered
                without a handler. */}
            {onRegenerate && (
              <>
                {/* The reason has to be legible, not just a dead button: the
                    left slot is already spoken for by the stale/dirty caption,
                    so the gate explains itself where the click lands. */}
                {isTurnBusy && (
                  <span className="text-meta">
                    Working — regenerate when this finishes
                  </span>
                )}
                {/* Locked while saving too, or its confirm becomes a lie: on a
                    stale AND dirty card you could Save, then Regenerate, agree
                    to "discard edits", and have the in-flight PUT persist them
                    anyway. `isTurnBusy` is the concurrency gate (3D §3.1a) —
                    `sendTurn` would otherwise clobber the module-singleton
                    abort controller and orphan the running stream. */}
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isSaving || isAccepting || isTurnBusy}
                  onClick={() => (isDirty ? setConfirmRegenerate(true) : onRegenerate())}
                >
                  Regenerate plan
                </Button>
              </>
            )}
            <Button
              size="sm"
              onClick={onAccept}
              disabled={isAccepting || isSaving || !onAccept || stale}
            >
              {isAccepting ? 'Accepting…' : 'Accept & draft'}
            </Button>
          </div>
        </div>
      )}
      {status === 'accepted' && (
        <div className="px-3 py-2 flex items-center gap-1.5 text-meta">
          <Check className="h-3 w-3 text-[hsl(var(--status-success))]" /> Plan accepted —
          drafting from this plan.
          {/* 3D §3.2 — accepting is no longer one-way. Same handler as
              Regenerate above; the card body stays rendered read-only, so the
              plan being replaced is still on screen while the writer decides. */}
          {onRegenerate && (
            <div className="ml-auto flex items-center gap-2">
              {/* Accept ends by sending the drafting turn, which sets
                  `isPolling` — so this button is disabled for most of the
                  window in which a writer would reach for it. Saying why is the
                  whole point: an ungated click just returns "Please wait for
                  the current background task to finish". */}
              {isTurnBusy && <span>Drafting — re-plan when this finishes</span>}
              <Button
                variant="ghost"
                size="sm"
                disabled={isTurnBusy}
                onClick={() => setConfirmReplan(true)}
              >
                Re-plan section
              </Button>
            </div>
          )}
        </div>
      )}
      {status === 'rejected' && (
        <div className="px-3 py-2 flex items-center gap-1.5 text-meta">
          <X className="h-3 w-3" /> Plan rejected — ask for a new plan when you're ready.
        </div>
      )}
    </>
  )

  const dialogs = (
    <>
      <PlanConfirmDialog
        open={confirmReject}
        onOpenChange={setConfirmReject}
        title="Reject this plan?"
        description="The plan is discarded and can't be recovered — including any edits you've made to it. You'll need to ask for a new plan before this section can be drafted."
        confirmLabel="Reject plan"
        isDestructive
        onConfirm={() => {
          setConfirmReject(false)
          onReject?.()
        }}
      />
      <PlanConfirmDialog
        open={confirmRegenerate}
        onOpenChange={setConfirmRegenerate}
        title="Regenerate plan?"
        description="Regenerating discards the edits you've made to this plan."
        // Named, not variant-promoted: the loss is irreversible, so the label
        // says what the click destroys rather than dressing it in red.
        confirmLabel="Regenerate and discard edits"
        onConfirm={() => {
          setConfirmRegenerate(false)
          onRegenerate?.()
        }}
      />
      {/* Deliberately NOT "this supersedes the draft being written" — that is
          false. `createOrReplaceDraft` inserts a new DRAFT plan row; the
          approved plan (and the draft running from it) is untouched until the
          new plan is itself approved. The real cost lands one step later, so
          that is what this says. */}
      <PlanConfirmDialog
        open={confirmReplan}
        onOpenChange={setConfirmReplan}
        title="Re-plan this section?"
        description="You'll get a new plan to review. The draft already being written from the accepted plan carries on unaffected. The cost comes later: if you accept the new plan, the drafter runs again over a section that already has content."
        // Names what the click actually does. Nothing is destroyed here — a
        // re-plan only adds a draft plan — so the label is plain, not a warning.
        confirmLabel="Create a new plan"
        onConfirm={() => {
          setConfirmReplan(false)
          onRegenerate?.()
        }}
      />
    </>
  )

  if (collapsedLabel) {
    // One card, header as the toggle: closed it is a single row ("Plan
    // approved · N words total"); open, the normal card body sits beneath it.
    return (
      <Collapsible
        open={isExpanded}
        onOpenChange={setIsExpanded}
        className="rounded-xl border bg-card shadow-sm divide-y"
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex w-full items-center justify-between gap-2 px-3 py-2 text-left',
              'rounded-xl data-[state=open]:rounded-b-none transition-colors hover:bg-muted/40',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            )}
          >
            <span className="flex items-center gap-1.5 text-sm font-medium">
              <Check className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-success))]" />
              {collapsedLabel}
            </span>
            <span className="flex items-center gap-2">
              <span className="text-meta">
                {totalWords.toLocaleString('en-AU')} words total
              </span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-subtle transition-transform',
                  isExpanded && 'rotate-180'
                )}
              />
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="divide-y">{body}</CollapsibleContent>
        {dialogs}
      </Collapsible>
    )
  }

  return (
    <div className="rounded-xl border bg-card shadow-sm divide-y">
      {header}
      {body}
      {dialogs}
    </div>
  )
}
